import React from 'react';
import type { ResultStrategy, WidgetDefinition, EvaluatePanelPropsBase } from './ResultStrategy';
import type { Layouts } from 'react-grid-layout';

// Widgets
import { LineChartWidget } from '../LineChartWidget';
import { RawDataTableWidget } from '../RawDataTableWidget';
import { HistogramWidget } from '../HistogramWidget';
import { AiInsightWidget } from '../AiInsightWidget';
import { WidgetWrapper } from '../WidgetWrapper';

export class TimeSeriesStrategy implements ResultStrategy {
    id = 'time-series';

    match(nature?: string): boolean {
        return !nature || ['generic', 'volume', 'signal'].includes(nature);
    }

    getAvailableWidgets(hasAiReport: boolean): WidgetDefinition[] {
        const widgets = [
            { id: 'chart', label: '📈 Chart', icon: '📈' },
            { id: 'table', label: '📋 Table', icon: '📋' },
            { id: 'summary', label: '📊 Stats', icon: '📊' },
            { id: 'histogram', label: '🎲 Histogram', icon: '🎲' },
        ];
        if (hasAiReport) {
            widgets.push({ id: 'ai-insights', label: '🤖 AI Insights', icon: '🤖' });
        }
        return widgets;
    }

    getDefaultWidgets(): string[] {
        return ["chart", "table", "summary", "histogram"];
    }

    getLayout(activeWidgets: string[]): Layouts {
        return {
            lg: [
                { i: "chart", x: 0, y: 0, w: activeWidgets.includes("ai-insights") ? 8 : 12, h: 6 },
                { i: "ai-insights", x: 8, y: 0, w: 4, h: 6 },
                { i: "table", x: 8, y: 6, w: 4, h: 4 },
                { i: "summary", x: 0, y: 6, w: 2, h: 4 },
                { i: "histogram", x: 2, y: 6, w: 6, h: 4 },
            ].filter((item) => activeWidgets.includes(item.i)),
            md: [
                { i: "chart", x: 0, y: 0, w: 12, h: 6 },
                { i: "ai-insights", x: 0, y: 6, w: 12, h: 6 },
                { i: "summary", x: 0, y: 12, w: 3, h: 4 },
                { i: "histogram", x: 3, y: 12, w: 9, h: 4 },
                { i: "table", x: 0, y: 16, w: 12, h: 4 },
            ].filter((item) => activeWidgets.includes(item.i)),
            sm: [
                { i: "chart", x: 0, y: 0, w: 12, h: 5 },
                { i: "ai-insights", x: 0, y: 5, w: 12, h: 6 },
                { i: "summary", x: 0, y: 11, w: 12, h: 4 },
                { i: "histogram", x: 0, y: 15, w: 12, h: 4 },
                { i: "table", x: 0, y: 19, w: 12, h: 4 },
            ].filter((item) => activeWidgets.includes(item.i)),
            xs: [
                { i: "chart", x: 0, y: 0, w: 12, h: 5 },
                { i: "ai-insights", x: 0, y: 5, w: 12, h: 6 },
                { i: "summary", x: 0, y: 11, w: 12, h: 4 },
                { i: "histogram", x: 0, y: 15, w: 12, h: 4 },
                { i: "table", x: 0, y: 19, w: 12, h: 4 },
            ].filter((item) => activeWidgets.includes(item.i)),
            xxs: [
                { i: "chart", x: 0, y: 0, w: 12, h: 5 },
                { i: "ai-insights", x: 0, y: 5, w: 12, h: 6 },
                { i: "summary", x: 0, y: 11, w: 12, h: 4 },
                { i: "histogram", x: 0, y: 15, w: 12, h: 4 },
                { i: "table", x: 0, y: 19, w: 12, h: 4 },
            ].filter((item) => activeWidgets.includes(item.i)),
        };
    }

    getStats(data: any, backendStats?: Record<string, number | null> | null): React.ReactNode {
        // Prefer backend-calculated stats if available
        if (backendStats) {
            const fmt = (v: number | null | undefined, digits = 4) =>
                v == null ? '—' : Number(v).toFixed(digits);
            return (
                <ul style={{ listStyle: "none", padding: 0, margin: 0, lineHeight: "1.8", color: 'var(--text-secondary)' }}>
                    <li><strong>Count:</strong> {backendStats.count ?? '—'}</li>
                    <li><strong>Min:</strong> {fmt(backendStats.min as number)}</li>
                    <li><strong>Max:</strong> {fmt(backendStats.max as number)}</li>
                    <li><strong>Average:</strong> {fmt(backendStats.avg as number)}</li>
                    <li><strong>Std Dev:</strong> {fmt(backendStats.std_dev as number)}</li>
                    <li title="Risk-adjusted return (higher = better)">
                        <strong>Sharpe Ratio:</strong> {fmt(backendStats.sharpe as number)}
                    </li>
                    <li title="Standardized distance of the latest value from the mean">
                        <strong>Z-score (latest):</strong> {fmt(backendStats.z_score as number)}
                    </li>
                </ul>
            );
        }

        // Fallback: client-side calculation
        if (!data?.length) return null;
        const values = data.map((d: any) => d.value).filter((v: any) => typeof v === "number");
        const count = values.length;
        if (count === 0) return null;
        const latest = values[values.length - 1];
        const min = Math.min(...values);
        const max = Math.max(...values);
        const avg = values.reduce((a: number, b: number) => a + b, 0) / count;
        const stddev = Math.sqrt(values.reduce((sum: number, val: number) => sum + Math.pow(val - avg, 2), 0) / count);
        const zScore = stddev > 0 ? (latest - avg) / stddev : 0;
        const returns = values.map((v: number, i: number) => i === 0 ? 0 : v - values[i - 1]);
        const meanReturn = returns.reduce((a: number, b: number) => a + b, 0) / returns.length;
        const stdReturn = Math.sqrt(returns.reduce((sum: number, val: number) => sum + Math.pow(val - meanReturn, 2), 0) / returns.length);
        const sharpe = stdReturn > 0 ? meanReturn / stdReturn : 0;

        return (
            <ul style={{ listStyle: "none", padding: 0, margin: 0, lineHeight: "1.8", color: 'var(--text-secondary)' }}>
                <li><strong>Count:</strong> {count}</li>
                <li><strong>Min:</strong> {min.toFixed(4)}</li>
                <li><strong>Max:</strong> {max.toFixed(4)}</li>
                <li><strong>Average:</strong> {avg.toFixed(4)}</li>
                <li><strong>Std Dev:</strong> {stddev.toFixed(4)}</li>
                <li title="Risk-adjusted return (higher = better)">
                    <strong>Sharpe Ratio:</strong> {sharpe.toFixed(4)}
                </li>
                <li title="Standardized distance of the latest value from the mean">
                    <strong>Z-score (latest):</strong> {zScore.toFixed(4)}
                </li>
            </ul>
        );
    }

    renderWidget(widgetId: string, props: EvaluatePanelPropsBase): React.ReactNode {
        switch (widgetId) {
            case 'chart':
                return (
                    <LineChartWidget
                        data={props.result}
                        average={props.average}
                        overlays={props.overlays}
                        setOverlays={props.setOverlays}
                        tunedResult={props.tunedResult}
                        strategyName={props.strategyName}
                        categorical_labels={props.categorical_labels}
                        display_type={props.display_type}
                        nature={props.nature}
                        globalStart={props.globalStart}
                        globalEnd={props.globalEnd}
                    />
                );
            case 'table':
                return (
                    <WidgetWrapper title="📋 Raw Table">
                        <RawDataTableWidget data={props.result} average={props.average} />
                    </WidgetWrapper>
                );
            case 'summary':
                return (
                    <WidgetWrapper title="📊 Statistics">
                        {this.getStats(props.result, props.backendStats)}
                    </WidgetWrapper>
                );
            case 'histogram':
                return (
                    <WidgetWrapper title="🎲 Histogram">
                        <HistogramWidget data={props.result} />
                    </WidgetWrapper>
                );
            case 'ai-insights':
                if (!props.currentAiReport) return null;
                return <AiInsightWidget report={props.currentAiReport} />;
            default:
                return null;
        }
    }
}
